import React, {
  InputHTMLAttributes,
  ReactNode,
  useEffect,
  useRef,
  useState
} from 'react'
import { toast } from 'react-toastify'
// import { ReactComponent as DownloadIcon } from 'presentation/assets/icons/DonwloadIcon.svg'
import { ReactComponent as UploadIcon } from 'presentation/assets/icons/upload.svg'
import { ReactComponent as CameraIcon } from 'presentation/assets/icons/blue-camera.svg'
import { ReactComponent as FolderIcon } from 'presentation/assets/icons/blue-folder.svg'
import { ReactComponent as LandscapeIcon } from 'presentation/assets/icons/blue-landscape.svg'
import { ReactComponent as DeleteIcon } from 'presentation/assets/icons/trash-outline-green.svg'
// import { ReactComponent as ViewIcon } from 'presentation/assets/icons/eye-dark-gray.svg'
import verifyValidMaxFileSizeInMb from 'common/utils/verify-max-file-size'
import SheetModal from '../SheetModal'
import * as S from './styles'

export type MultimediaFileInputProps = {
  name: string
  text?: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onDelete?: () => void
  span?: ReactNode
  fileName?: string
  showImagePreview?: boolean
  maxFileSizeMB?: number
  errorMessage?: string
} & InputHTMLAttributes<HTMLInputElement>

const MultimediaFileInputNew = ({
  text = 'Carregar documento',
  showImagePreview = false,
  span,
  errorMessage,
  maxFileSizeMB,
  ...props
}: MultimediaFileInputProps) => {
  const fileCameraInputRef = useRef(null)
  const fileGalleryInputRef = useRef(null)
  const fileDocsInputRef = useRef(null)
  const [fileName, setFileName] = useState(props.fileName ?? '')
  const [fileExt, setFileExt] = useState('')
  const [fileSrc, setFileSrc] = useState<any>('')
  const [openSheetModal, setOpenSheetModal] = useState(false)

  useEffect(() => {
    const pattern = /\.([0-9a-z]+)(?:[?#]|$)/i
    const match = fileName.match(pattern)
    if (props.fileName && match) {
      setFileExt(match[1])
    }
  }, [fileName])

  if (fileName) {
    errorMessage = ''
  }

  function onHandleChange(event: any) {
    event.stopPropagation()
    event.preventDefault()
    if (event.target.files.length > 0) {
      const file = event.target.files[0]
      const { name, type, size } = file
      if (hasSizeError(size)) {
        setOpenSheetModal(false)
        toast.error(`Arquivo não pode ser maior que ${maxFileSizeMB} MB.`, {
          toastId: name
        })
        return
      }
      const fileReader = new FileReader()
      fileReader.onload = () => {
        setFileSrc(fileReader.result)
      }
      fileReader.readAsDataURL(file)
      setFileName(name)
      setFileExt(type.split('/')[1])
      !!props.onChange && props.onChange(event)
    }
    setOpenSheetModal(false)
  }

  function handleDeleteFile() {
    setFileName('')
    setFileExt('')
    setFileSrc('')
    const fileCamInput: any = fileCameraInputRef.current
    const fileGalleryInput: any = fileGalleryInputRef.current
    const fileDocsInput: any = fileDocsInputRef.current
    fileCamInput.value = ''
    fileGalleryInput.value = ''
    fileDocsInput.value = ''
    !!props.onDelete && props.onDelete()
  }

  function hasSizeError(fileSize: number) {
    if (!maxFileSizeMB) {
      return false
    }
    return !verifyValidMaxFileSizeInMb(fileSize, maxFileSizeMB)
  }

  useEffect(() => {
    setFileName(props.fileName ?? '')
  }, [props.fileName])

  return (
    <>
      <S.Wrapper
        {...props}
        disabled={!!props.disabled}
        file={!!fileName}
        error={!!errorMessage}
      >
        <S.Label
          disabled={!!props.disabled}
          data-testid="container"
          onClick={() => !props.disabled && setOpenSheetModal(true)}
        />
        <S.Content>
          {fileName ? (
            <S.FileWrapper>
              <S.Preview>
                {showImagePreview && fileExt != 'pdf' ? (
                  <S.ImagePreview
                    src={fileSrc}
                    alt="Pre-visualização do arquivo"
                  />
                ) : (
                  <S.FileExtBox fileExt={fileExt}>
                    <small data-testid="text-file-extension">{fileExt}</small>
                  </S.FileExtBox>
                )}
              </S.Preview>
              <S.FileName>{fileName && fileName}</S.FileName>
            </S.FileWrapper>
          ) : (
            <S.UploadWrapper>
              <UploadIcon height={18} />
              <S.UploadText>{text}</S.UploadText>
            </S.UploadWrapper>
          )}
          {!!span && !fileName && span}
        </S.Content>
        {/* TODO: Make optional inputs (Image, Gallery, Documents...) */}
        <input
          ref={fileCameraInputRef}
          type="file"
          accept="image/*"
          id={`camera-${props.name}`}
          capture="environment"
          onChange={onHandleChange}
          // onInput={(e) => onHandleChange(e)}
          disabled={props.disabled}
          name={props.name}
          data-testid="camera-input"
        />
        <input
          ref={fileGalleryInputRef}
          type="file"
          accept="image/*"
          id={`gallery-${props.name}`}
          onChange={onHandleChange}
          // onInput={(e) => onHandleChange(e)}
          disabled={props.disabled}
          name={props.name}
        />
        <input
          ref={fileDocsInputRef}
          type="file"
          accept=".pdf,.doc,.docx" // TODO: Make property dynamic
          id={`docs-${props.name}`}
          onChange={onHandleChange}
          // onInput={(e) => onHandleChange(e)}
          disabled={props.disabled}
          name={props.name}
        />
        <SheetModal
          isOpen={openSheetModal}
          isDraggable={true}
          onClose={() => setOpenSheetModal(false)}
          size={200}
        >
          {/* TODO: Make optional inputs (Image, Gallery, Documents...) */}
          <S.TypeList>
            <S.TypeListItem
              htmlFor={`camera-${props.name}`}
              data-testid="list-item-take-picture"
            >
              <CameraIcon />
              <span>Tirar foto</span>
            </S.TypeListItem>
            <S.TypeListItem
              htmlFor={`gallery-${props.name}`}
              data-testid="list-item-gallery"
            >
              <LandscapeIcon />
              <span>Galeria de fotos</span>
            </S.TypeListItem>
            <S.TypeListItem
              htmlFor={`docs-${props.name}`}
              data-testid="list-item-documents"
            >
              <FolderIcon />
              <span>Documentos</span>
            </S.TypeListItem>
          </S.TypeList>
        </SheetModal>
      </S.Wrapper>
      {Boolean(!fileName) && Boolean(errorMessage) && (
        <S.ErrorMessageSpan data-testid="multimedia-error-message">
          {errorMessage}
        </S.ErrorMessageSpan>
      )}
      {fileName && !!props.onDelete && (
        <S.FileActions>
          {/* <DownloadIcon width={24} height={24} /> */}
          {/* <ViewIcon
            width={24}
            height={24}
            style={{ cursor: 'pointer' }}
            onClick={() => window.open(fileSrc)}
          /> */}
          <DeleteIcon
            width={24}
            height={24}
            onClick={handleDeleteFile}
            style={{ cursor: 'pointer' }}
          />
        </S.FileActions>
      )}
    </>
  )
}

export default MultimediaFileInputNew
